// X-wing detector. For a digit `d`, find two base units (both rows OR both
// cols) where `d` has exactly two candidate cells, and those cells align on
// the same two cross-units. Then `d` must occupy a column-paired pattern
// inside the four corners, so it can be eliminated from those two
// cross-units in every other unit.

import { BOARD_SIZE, type NonEmptyDigit } from './types.js';
import { bit, type CandidateState } from './Candidates.js';
import type { Elimination } from './SolverTechniques.js';

export interface XWingStep {
  readonly technique: 'x-wing';
  readonly digit: NonEmptyDigit;
  readonly orientation: 'row-based' | 'column-based';
  /** Row indices (row-based) or column indices (column-based). */
  readonly baseUnits: readonly [number, number];
  /** The two cross-units (cols for row-based, rows for column-based). */
  readonly coverUnits: readonly [number, number];
  /** Corner cells in [b1-c1, b1-c2, b2-c1, b2-c2] order. */
  readonly cornerCells: readonly [number, number, number, number];
  readonly eliminations: readonly Elimination[];
}

function cellIndex(row: number, col: number): number {
  return row * BOARD_SIZE + col;
}

// Cross-positions (cols if base=row, rows if base=col) where digit `d` is a
// candidate within base unit `base`. Returns null if the count is not exactly 2.
function exactTwoCrossPositions(
  base: number,
  digitBit: number,
  state: CandidateState,
  baseIsRow: boolean,
): readonly [number, number] | null {
  const positions: number[] = [];
  for (let k = 0; k < BOARD_SIZE; k++) {
    const i = baseIsRow ? cellIndex(base, k) : cellIndex(k, base);
    if (((state.cells[i] ?? 0) & digitBit) !== 0) {
      positions.push(k);
      if (positions.length > 2) return null;
    }
  }
  if (positions.length !== 2) return null;
  return [positions[0] as number, positions[1] as number];
}

function collectEliminations(
  baseUnits: readonly [number, number],
  coverUnits: readonly [number, number],
  digit: NonEmptyDigit,
  state: CandidateState,
  baseIsRow: boolean,
): Elimination[] {
  const digitBit = bit(digit);
  const out: Elimination[] = [];
  for (let other = 0; other < BOARD_SIZE; other++) {
    if (other === baseUnits[0] || other === baseUnits[1]) continue;
    for (const cover of coverUnits) {
      const i = baseIsRow ? cellIndex(other, cover) : cellIndex(cover, other);
      if (((state.cells[i] ?? 0) & digitBit) !== 0) {
        out.push({ cellIndex: i, digit });
      }
    }
  }
  return out;
}

function findXWingForOrientation(
  state: CandidateState,
  baseIsRow: boolean,
): XWingStep | null {
  for (let n = 1; n <= BOARD_SIZE; n++) {
    const digit = n as NonEmptyDigit;
    const digitBit = bit(digit);
    const candidateRows: Array<{ base: number; pair: readonly [number, number] }> = [];
    for (let base = 0; base < BOARD_SIZE; base++) {
      const pair = exactTwoCrossPositions(base, digitBit, state, baseIsRow);
      if (pair !== null) candidateRows.push({ base, pair });
    }
    for (let i = 0; i < candidateRows.length; i++) {
      const a = candidateRows[i];
      if (a === undefined) continue;
      for (let j = i + 1; j < candidateRows.length; j++) {
        const b = candidateRows[j];
        if (b === undefined) continue;
        if (a.pair[0] !== b.pair[0] || a.pair[1] !== b.pair[1]) continue;
        const baseUnits: readonly [number, number] = [a.base, b.base];
        const coverUnits: readonly [number, number] = a.pair;
        const eliminations = collectEliminations(baseUnits, coverUnits, digit, state, baseIsRow);
        if (eliminations.length === 0) continue;
        const cornerCells: readonly [number, number, number, number] = baseIsRow
          ? [
              cellIndex(baseUnits[0], coverUnits[0]),
              cellIndex(baseUnits[0], coverUnits[1]),
              cellIndex(baseUnits[1], coverUnits[0]),
              cellIndex(baseUnits[1], coverUnits[1]),
            ]
          : [
              cellIndex(coverUnits[0], baseUnits[0]),
              cellIndex(coverUnits[1], baseUnits[0]),
              cellIndex(coverUnits[0], baseUnits[1]),
              cellIndex(coverUnits[1], baseUnits[1]),
            ];
        return {
          technique: 'x-wing',
          digit,
          orientation: baseIsRow ? 'row-based' : 'column-based',
          baseUnits,
          coverUnits,
          cornerCells,
          eliminations,
        };
      }
    }
  }
  return null;
}

export function findXWing(state: CandidateState): XWingStep | null {
  const rowBased = findXWingForOrientation(state, true);
  if (rowBased !== null) return rowBased;
  return findXWingForOrientation(state, false);
}
